'use client';

import { useState } from 'react';
import Link from 'next/link';
import { X, Eye, Share2, Heart, ChevronLeft } from 'lucide-react';
import MuxPlayer from '@/components/posts/MuxPlayer';
import { UserVideoPost, UserProfile } from '@/lib/services/user-profile-service';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { cn } from '@/lib/utils';

interface PostDetailViewProps {
  post: UserVideoPost;
  profile: UserProfile;
  handle: string;
  isPreview?: boolean;
}

export default function PostDetailView({ post, profile, handle, isPreview = false }: PostDetailViewProps) {
  const [isLiked, setIsLiked] = useState(false);
  const searchParams = useSearchParams();
  const from = searchParams.get('from');
  
  // Determine where to go back based on the 'from' parameter
  const backUrl = from === 'home' ? '/home' : `/@${handle}`;
  
  // Auto-play with sound when coming from home (user initiated navigation)
  const shouldAutoPlay = from === 'home';
  
  const handleShare = async () => {
    const url = `${window.location.origin}/@${handle}/posts/${post.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.subtitle || post.description || '',
          url: url
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(url);
      // You could show a toast notification here
    }
  };
  
  const formatDuration = (seconds?: number | null) => {
    if (!seconds) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="container max-w-7xl mx-auto px-2 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isPreview && (
              <Button variant="ghost" size="icon" asChild className="sm:hidden">
                <Link href={backUrl}>
                  <ChevronLeft className="h-5 w-5" />
                  <span className="sr-only">Back</span>
                </Link>
              </Button>
            )}
            
            <Link 
              href={isPreview ? '#' : `/@${handle}`} 
              className={cn(
                "flex items-center gap-3 transition-opacity",
                !isPreview && "hover:opacity-80"
              )}
              onClick={isPreview ? (e) => e.preventDefault() : undefined}
            >
              {profile.avatar_url ? (
                <Image
                  src={profile.avatar_url}
                  alt={profile.name}
                  width={40}
                  height={40}
                  className="rounded-full"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-semibold">
                  {profile.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="hidden sm:block">
                <p className="font-medium">{profile.name}</p>
                <p className="text-sm text-muted-foreground">@{profile.handle}</p>
              </div>
            </Link>
          </div>
          
          {!isPreview && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShare}
                className="hover:bg-muted"
              >
                <Share2 className="h-5 w-5" />
                <span className="sr-only">Share</span>
              </Button>
              
              <Button variant="ghost" size="icon" asChild className="hover:bg-muted">
                <Link href={backUrl}>
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="lg:container lg:max-w-7xl lg:mx-auto px-2 sm:px-4 py-4 sm:py-6 lg:py-8">
        <div className="flex flex-col lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Main Content - Video and Info */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Video Player - Full width on mobile */}
            <div className="aspect-video rounded-lg overflow-hidden bg-black -mx-2 sm:mx-0">
              <MuxPlayer
                playbackId={post.mux_playback_id!}
                title={post.title}
                autoPlay={shouldAutoPlay}
                muted={false}
                controls={true}
                className="w-full h-full"
              />
            </div>
            
            {/* Video Info - Mobile first layout */}
            <div className="px-2 sm:px-0 space-y-3 sm:space-y-4">
              <div>
                <h1 className="text-lg sm:text-xl lg:text-3xl font-bold">{post.title}</h1>
                {post.subtitle && (
                  <p className="text-sm sm:text-base lg:text-lg text-muted-foreground mt-1 sm:mt-2">{post.subtitle}</p>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:gap-6 text-xs sm:text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                  {post.view_count || 0} views
                </span>
                <span>
                  {formatDistanceToNow(new Date(post.publish_at || post.created_at), { addSuffix: true })}
                </span>
                {post.duration && (
                  <span>{formatDuration(post.duration)}</span>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t">
                <Button 
                  variant={isLiked ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setIsLiked(!isLiked)}
                  className="h-8 sm:h-9 text-xs sm:text-sm px-3 sm:px-4"
                >
                  <Heart className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 ${isLiked ? 'fill-current' : ''}`} />
                  {isLiked ? 'Liked' : 'Like'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleShare}
                  className="h-8 sm:h-9 text-xs sm:text-sm px-3 sm:px-4"
                >
                  <Share2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                  Share
                </Button>
              </div>
              
              {/* Mobile: About sections directly after buttons */}
              <div className="lg:hidden space-y-3 sm:space-y-4 pt-2">
                {/* About this video - Mobile */}
                {post.description && (
                  <div className="bg-muted/50 rounded-lg p-3 sm:p-4">
                    <h3 className="text-sm sm:text-base font-semibold mb-2">About this video</h3>
                    <p className="text-xs sm:text-sm whitespace-pre-wrap leading-relaxed">{post.description}</p>
                  </div>
                )}
                
                {/* Creator info - Mobile */}
                <div className="bg-muted/50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3">About the creator</h3>
                  <Link href={`/@${handle}`} className="group">
                    <div className="flex items-center gap-2 sm:gap-3">
                      {profile.avatar_url ? (
                        <Image
                          src={profile.avatar_url}
                          alt={profile.name}
                          width={48}
                          height={48}
                          className="rounded-full w-8 h-8 sm:w-10 sm:h-10"
                        />
                      ) : (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted flex items-center justify-center text-xs sm:text-sm font-semibold">
                          {profile.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium group-hover:text-primary transition-colors truncate">{profile.name}</p>
                        <p className="text-xs text-muted-foreground truncate">@{profile.handle}</p>
                      </div>
                    </div>
                    {profile.bio && (
                      <p className="text-xs sm:text-sm mt-2 line-clamp-2 leading-relaxed">{profile.bio}</p>
                    )}
                  </Link>
                  <Button variant="outline" size="sm" className="mt-3 w-full h-8 text-xs" asChild>
                    <Link href={`/@${handle}`}>
                      View Profile
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Desktop Sidebar - Hidden on mobile, shown on lg screens */}
          <div className="hidden lg:block lg:space-y-6">
            {/* About this video - Desktop */}
            {post.description && (
              <div className="bg-muted/50 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-3">About this video</h3>
                <p className="text-sm whitespace-pre-wrap">{post.description}</p>
              </div>
            )}
            
            {/* Creator info - Desktop */}
            <div className="bg-muted/50 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">About the creator</h3>
              <Link href={`/@${handle}`} className="group">
                <div className="flex items-center gap-3">
                  {profile.avatar_url ? (
                    <Image
                      src={profile.avatar_url}
                      alt={profile.name}
                      width={48}
                      height={48}
                      className="rounded-full"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-lg font-semibold">
                      {profile.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-medium group-hover:text-primary transition-colors">{profile.name}</p>
                    <p className="text-sm text-muted-foreground">@{profile.handle}</p>
                  </div>
                </div>
                {profile.bio && (
                  <p className="text-sm mt-3 line-clamp-3">{profile.bio}</p>
                )}
              </Link>
              <Button variant="outline" size="sm" className="mt-4 w-full" asChild>
                <Link href={`/@${handle}`}>
                  View Profile
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}